import React, { useState, useEffect } from 'react';
import { client, urlFor } from '../../client'; // Import Sanity client and urlFor
import { PortableText } from '@portabletext/react'; // Import PortableText

// Consistent Loading Spinner
const LoadingSpinner = () => (
  <div className="flex justify-center items-center min-h-screen">
    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-[#2a4865]"></div>
    <p className="ml-4 text-lg text-gray-700">Loading...</p>
  </div>
);

// Consistent Error Message
const ErrorMessage = ({ message }) => (
  <div className="container mx-auto px-4 py-12 text-center">
    <p className="text-red-600 font-semibold">{message || 'Error loading page content. Please try again later.'}</p>
  </div>
);

// Simple Portable Text components (default strong should work for the intro)
const ptComponents = {
  marks: {
    // Default strong handling should be sufficient for font-medium in this case
  }
};


const BusInfo = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPageData = async () => {
      setLoading(true);
      try {
        const query = `*[_type == "busInfoPage" && _id == "busInfoPageContent"][0] {
          bannerTitle,
          bannerImage {
            asset->{
              _id,
              url
            }
          },
          mainContentTitle,
          mainContentIntro,
          transportationOverviewLink,
          additionalResourcesTitle,
          resourceBoxes[]{
            _key,
            title,
            description,
            linkText,
            linkUrl
          },
          contactPanelTitle,
          contactPanelText,
          contactPhoneNumber,
          contactEmail
        }`;
        const data = await client.fetch(query);
        if (data) {
          setPageData(data);
        } else {
          setError('Bus Information Page content not found.');
        }
        setLoading(false);
      } catch (err) {
        console.error("Failed to fetch Bus Information page data:", err);
        setError(err.message || 'Failed to load page content. Please try again later.');
        setLoading(false);
      }
    };

    fetchPageData();
  }, []);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;
  if (!pageData) return <ErrorMessage message="No content available for this page." />;

  return (
    <div className="bg-white">
      {/* Hero Banner with Image Overlay - Dynamic */}
      <section className="relative h-[40vh] max-h-[400px]">
        <div className="w-full h-full relative overflow-hidden">
           {pageData.bannerImage?.asset?.url ? (
            <img 
              src={urlFor(pageData.bannerImage).url()} 
              alt={pageData.bannerTitle || 'PS52 School Bus Information'} 
              className="absolute inset-0 w-full h-full object-cover object-center" 
              loading="lazy"
            />
          ) : (
            <div className="absolute inset-0 w-full h-full bg-gray-300 flex items-center justify-center">
              <span className="text-gray-500">Banner Image Missing</span>
            </div> 
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/20"></div>
        </div>
        
        <div className="absolute inset-0 flex items-end">
          <div className="w-full px-6 md:px-12 lg:px-16">
            <div className="max-w-3xl pb-12 md:pb-16 lg:pb-16 text-left">
              <div className="w-16 h-1 bg-yellow-500 mb-6"></div>
              
              <h1 className="text-white text-4xl md:text-5xl font-bold mb-2 leading-tight text-left">
                {pageData.bannerTitle || 'Bus Information'} {/* Dynamic */} 
              </h1>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="bg-white p-8 rounded-lg shadow-md max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
              {/* Main Content Column - Dynamic */}
              <div className="md:col-span-8">
                <div className="space-y-8">
                  {pageData.mainContentTitle && (
                    <div>
                      <h2 className="text-2xl font-bold text-[#2a4865] mb-6 relative after:content-[''] after:absolute after:w-16 after:h-1 after:bg-yellow-500 after:-bottom-3 after:left-0">
                        {pageData.mainContentTitle}
                      </h2>
                      
                      {/* Render intro text using PortableText */} 
                      {pageData.mainContentIntro && (
                        <div className="mt-8 text-gray-700 leading-relaxed prose prose-sm sm:prose max-w-none">
                            <PortableText value={pageData.mainContentIntro} components={ptComponents} />
                        </div>
                      )}
                      
                      {/* Render Transportation Overview Link */} 
                      {pageData.transportationOverviewLink && (
                        <div className="mt-4 mb-6">
                          <a 
                            href={pageData.transportationOverviewLink} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="inline-flex items-center text-[#691e31] hover:text-[#8a2c43] transition-colors font-medium"
                          >
                            {/* Use URL as link text to match original */} 
                            <span className="border-b border-current">{pageData.transportationOverviewLink}</span>
                             {/* Keep exact SVG icon */}
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                          </a>
                        </div>
                      )}
                      
                      {/* Removed hardcoded NYCSA paragraph, assuming it's covered by mainContentIntro */} 

                      {/* Render Additional Resources - Dynamic */} 
                      {pageData.additionalResourcesTitle && pageData.resourceBoxes?.length > 0 && (
                        <>
                          <h3 className="text-xl font-bold text-[#2a4865] mb-4 mt-8"> {/* Added margin top */} 
                            {pageData.additionalResourcesTitle}
                          </h3>
                          <div className="space-y-4 mt-4">
                            {pageData.resourceBoxes.map(box => (
                              <div key={box._key} className="bg-gray-50 p-4 rounded-lg border-l-4 border-[#691e31]">
                                <h4 className="font-medium text-[#2a4865] mb-2">{box.title}</h4>
                                <p className="text-gray-700 mb-3">{box.description}</p>
                                <a 
                                  href={box.linkUrl}
                                  target="_blank" 
                                  rel="noopener noreferrer" 
                                  className="inline-flex items-center text-[#691e31] hover:text-[#8a2c43] transition-colors font-medium"
                                >
                                  <span className="border-b border-current">{box.linkText}</span>
                                   {/* Keep exact SVG icon */} 
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                  </svg>
                                </a>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Contact Side Panel - Dynamic */} 
              {pageData.contactPanelTitle && (
                <div className="md:col-span-4">
                  <div className="bg-[#2a4865] text-white p-6 rounded-lg h-full">
                    <div className="flex items-center mb-6">
                       {/* Keep exact styling */} 
                      <span className="w-1 h-8 bg-yellow-500 mr-3"></span>
                      <h3 className="text-xl font-bold text-white">{pageData.contactPanelTitle}</h3>
                    </div>
                    
                    {pageData.contactPanelText && (
                       <div className="bg-[#1e3550] p-5 rounded-lg">
                        <p className="leading-relaxed text-white">
                          {pageData.contactPanelText} 
                        </p>
                        
                        <div className="mt-4 space-y-3">
                          {pageData.contactPhoneNumber && (
                            <div className="flex items-center">
                              {/* Keep exact SVG icon */}
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                              </svg>
                              <span className="font-medium text-white">{pageData.contactPhoneNumber}</span>
                            </div>
                          )}
                          
                          {pageData.contactEmail && (
                            <div className="flex items-center">
                               {/* Keep exact SVG icon */}
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                              <a 
                                href={`mailto:${pageData.contactEmail}`}
                                className="text-yellow-400 hover:text-yellow-300 transition-colors"
                              >
                                {pageData.contactEmail}
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BusInfo; 